
import { useState } from 'react';
import { toast } from 'sonner';
import { createPaper } from '@/lib/api/paper-mutations/create-paper';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { ArxivRecord } from '../types';
import { PaperAuthor } from '@/types/paper';

export const useArxivImport = () => {
  const [isImporting, setIsImporting] = useState(false);
  const { user } = useAuth();

  const triggerBackgroundProcessing = async (paperId: string, arxivId: string, title: string, abstract: string) => {
    try {
      // Construct the arXiv PDF URL
      const arxivPdfUrl = `https://arxiv.org/pdf/${arxivId}.pdf`;
      
      console.log(`Starting background processing for paper ${paperId} with PDF: ${arxivPdfUrl}`);

      // Trigger keyword and research information extraction using extract-paper-data
      const extractPromise = supabase.functions.invoke('extract-paper-data', {
        body: {
          paperId,
          pdfUrl: arxivPdfUrl,
          title,
          abstract,
          currentKeywords: [],
          externalUrl: `https://arxiv.org/abs/${arxivId}`
        }
      });

      // Wait for extraction function to be triggered (not completed)
      const extractResponse = await extractPromise;

      if (extractResponse.error) {
        console.warn('Failed to trigger keyword and research info extraction:', extractResponse.error);
      } else {
        console.log('Keyword and research info extraction triggered successfully');
      }

      // Show info about background processing
      if (!extractResponse.error) {
        toast.info('Background processing started', {
          description: 'Keywords and research information are being generated from the PDF. This may take a few minutes.',
          duration: 8000
        });
      }

    } catch (error) {
      console.error('Error triggering background processing:', error);
      // Don't show error toast as this shouldn't block the main import flow
    }
  };

  const importPaper = async (record: ArxivRecord) => {
    if (!user) {
      toast.error('You must be logged in to import papers');
      return;
    }

    setIsImporting(true);
    try {
      // Convert arXiv authors to PaperAuthor format
      // Only include the actual paper authors, not the admin who is importing
      const authors: PaperAuthor[] = record.authors.map((authorName, index) => ({
        id: `arxiv-author-${record.id}-${index}`, // Temporary ID for non-platform users
        name: authorName,
        isPlatformUser: false,
        institution: 'Unknown', // arXiv doesn't provide institution info
        role: 'Author'
      }));

      // Determine the primary category for the paper
      const primaryCategory = record.categories.length > 0 ? record.categories[0] : 'cs';
      
      // Convert arXiv categories to keywords
      const keywords = record.categories;

      // Create the external URL
      const externalUrl = `https://arxiv.org/abs/${record.id}`;

      // Import the paper
      // The current user will be set as the uploader (author_id) but NOT added to the authors array
      const result = await createPaper(
        record.title,
        record.abstract,
        user.id, // Current user becomes the uploader (for database relationship)
        primaryCategory,
        keywords,
        undefined, // No file URL since it's external
        externalUrl,
        authors, // Only the actual paper authors, not the admin
        'public',
        false,
        [],
        record.publishedDate // Use original arXiv publication date
      );

      if (result) {
        toast.success(`Paper "${record.title}" imported successfully!`, {
          description: 'The arXiv paper has been added to the database.',
          duration: 5000
        });

        // Trigger background processing for PDF analysis (keywords and research info only)
        // Note: AI review generation is already handled in createPaper for arXiv papers
        await triggerBackgroundProcessing(result.id, record.id, record.title, record.abstract);
      } else {
        toast.error('Failed to import paper', {
          description: 'There was an error adding the paper to the database.'
        });
      }
    } catch (error) {
      console.error('Error importing arXiv paper:', error);
      toast.error('Import failed', {
        description: error instanceof Error ? error.message : 'An unexpected error occurred'
      });
    } finally {
      setIsImporting(false);
    }
  };

  return {
    importPaper,
    isImporting
  };
};
